<template>
    <div class="bg-elevated">
        <UContainer>
            <div class="flex justify-start h-96">
                <div class="basis-1/2 self-center space-y-4">
                    <h1 class="font-bold text-6xl">Contact</h1>
                    <p>
                        If you have any questions or inquiries, feel free to reach out to us!
                    </p>
                </div>
                <div class="basis-1/2 text-center self-center">
                    <UIcon name="i-heroicons-envelope" class="size-48 opacity-35" />
                </div>
            </div>
        </UContainer>
    </div>

    <UContainer class="my-16">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-lg-2 xl:pt-0">
            
            <!-- Location -->
            <div class="col">
                <div class="flex items-center">
                    <UIcon name="i-heroicons-map-pin" class="text-lg text-gray-900 dark:text-gray-100" />
                    <h3 class="text-base font-semibold ps-2 ms-1 mb-0">Store location</h3>
                </div>
                <hr class="border-gray-900 dark:border-gray-100 opacity-50 my-3 md:my-4" />
                <ul class="list-none p-0">
                    <li>New York 11741, USA</li>
                    <li>396 Lillian Bolavandy, Holbrook</li>
                </ul>
            </div>

            <!-- Phones -->
            <div class="col">
                <div class="flex items-center">
                    <UIcon name="i-heroicons-phone" class="text-lg text-gray-900 dark:text-gray-100" />
                    <h3 class="text-base font-semibold ps-2 ms-1 mb-0">Call us directly</h3>
                </div>
                <hr class="border-gray-900 dark:border-gray-100 opacity-50 my-3 md:my-4" />
                <ul class="list-none p-0">
                    <li>Customers: 1 50 537 53 082</li>
                    <li>Franchise: 1 50 537 53 000</li>
                </ul>
            </div>

            <!-- Emails -->
            <div class="col">
                <div class="flex items-center">
                    <UIcon name="i-heroicons-envelope" class="text-lg text-gray-900 dark:text-gray-100" />
                    <h3 class="text-base font-semibold ps-2 ms-1 mb-0">Send a message</h3>
                </div>
                <hr class="border-gray-900 dark:border-gray-100 opacity-50 my-3 md:my-4" />
                <ul class="list-none p-0">
                    <li>Customers: help@foo.com</li>
                    <li>Franchise: franchise@foo.com</li>
                </ul>
            </div>

            <!-- Working hours -->
            <div class="col">
                <div class="flex items-center">
                    <UIcon name="i-heroicons-clock" class="text-lg text-gray-900 dark:text-gray-100" />
                    <h3 class="text-base font-semibold ps-2 ms-1 mb-0">Working hours</h3>
                </div>
                <hr class="border-gray-900 dark:border-gray-100 opacity-50 my-3 md:my-4" />
                <ul class="list-none p-0">
                    <li>Mon - Fri  8:00 - 18:00</li>
                    <li>Sat - Sun  10:00 - 16:00</li>
                </ul>
            </div>
        </div>
    </UContainer>

    <div class="mt-24">
        <UContainer class="flex flex-col gap-2 md:flex-row justify-between items-start mb-8">
            <div class="space-y-4 mb-8">
                <h2 class="text-3xl font-bold">
                    Looking for support?
                </h2>
                <p>
                    We might already have what you're looking for. See our FAQs or head to our dedicated Help Center.
                </p>
            </div>
            <UButton variant="outline" color="neutral" size="lg" class="py-4 px-6">
                Visit Help Center
            </UButton>
        </UContainer>

          <ClientOnly
      fallback-tag="span"
      fallback="Loading comments..."
    >
      <Map class="z-0" />
    </ClientOnly>
    </div>

    <UContainer>
        <h2 class="text-3xl font-bold text-center my-12">
            Frequently Asked Questions
        </h2>
        <UAccordion :items="items" class="max-w-2xl mx-auto" />
    </UContainer>

    <UContainer>
        <Instagram class="mt-12" />
    </UContainer>

</template>
<script setup lang="ts">
definePageMeta({
  layout: 'shop'
})

import Instagram from '~/components/organisms/Instagram.vue';
import Map from '~/components/organisms/Map.vue'

const items = ref([
  {
    label: 'How long will delivery take?',
    content: 'Delivery times vary based on your location and the chosen shipping method. Generally, our standard delivery takes up to 5 days, while our Express Delivery ensures your order reaches you within 1 day. Please note that these times may be subject to occasional variations due to unforeseen circumstances, but we do our best to meet these estimates.'
  },
  {
    label: 'What payment methods do you accept?',
    content: 'We offer a range of secure payment options to provide you with flexibility and convenience. Accepted methods include major credit/debit cards, PayPal, and other secure online payment gateways. You can find the complete list of accepted payment methods during the checkout process.'
  },
  {
    label: 'Do you ship internationally?',
    content: 'Yes, we proudly offer international shipping to cater to our global customer base. Shipping costs and delivery times will be automatically calculated at the checkout based on your selected destination. Please note that any customs duties or taxes applicable in your country are the responsibility of the customer.'
  },
  {
    label: 'Do I need an account to place an order?',
    content: 'While you can place an order as a guest, creating an account comes with added benefits. By having an account, you can easily track your orders, manage your preferences, and enjoy a quicker checkout process for future purchases. It also allows us to provide you with personalized recommendations and exclusive offers.'
  },
  {
    label: 'How can I track my order?',
    content: 'Once your order is dispatched, you will receive a confirmation email containing a unique tracking number. You can use this tracking number on our website to monitor the real-time status of your shipment. Additionally, logging into your account will grant you access to a comprehensive order history, including tracking information.'
  },
  {
    label: 'What are the product refund conditions?',
    content: 'Our refund policy is designed to ensure customer satisfaction. Details can be found in our [refund policy page](insert link). In essence, we accept returns within [insert number] days of receiving the product, provided it is in its original condition with all tags and packaging intact. Refunds are processed promptly once the returned item is inspected and approved.'
  },
  {
    label: 'Do I need to create an account to shop with you?',
    content: 'While guest checkout is available for your convenience, creating an account enhances your overall shopping experience. With an account, you can easily track your order status, save multiple shipping addresses, and enjoy a streamlined checkout process. Moreover, account holders receive early access to promotions and exclusive offers. Signing up is quick and hassle-free!'
  },
  {
    label: 'Is there a minimum order value for free shipping?',
    content: 'Yes, we offer free shipping on orders exceeding $250. Orders below this threshold are subject to standard shipping fees, which will be displayed during the checkout process.'
  }
])

</script>